import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { CaseSummary, DesignParameter } from '@/types';

/**
 * The case behind the model: what is being made, how, and what the technician
 * said about it - folded out of the way of the model.
 *
 * Parameters are shown against the range their material allows, and the ones
 * the technician moved off the material's standard are marked - those are the
 * decisions a dentist might want to question. Only the five that decide fit
 * are shown; the rest are milling settings a dentist does not act on. A design
 * that sent none has no parameters section at all, rather than an empty one.
 */
const HEADLINE = [
  'min_thickness',
  'occlusal_thickness',
  'cement_gap_width',
  'margin_horizontal',
  'distance_antagonist',
  // NuCAD's own names, which is what the desktop sends.
  'min_wall_thickness',
  'cement_gap',
  'non_cement_band',
  'border_horizontal',
  'border_vertical',
  'border_angle',
];

function Section({
  title,
  aside,
  children,
  defaultOpen = true,
}: {
  title: string;
  aside?: ReactNode;
  children: ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className="border-b border-border last:border-b-0">
      <button
        type="button"
        onClick={() => {
          setOpen((was) => !was);
        }}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <h3 className="text-[12px] font-semibold tracking-wide text-muted-foreground uppercase">
          {title}
        </h3>
        {aside}
        <ChevronDown
          className={cn(
            'ml-auto size-4 text-muted-foreground transition-transform',
            !open && '-rotate-90',
          )}
        />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </section>
  );
}

function formatValue(value: number, unit: string): string {
  const digits = unit === 'degree' || Number.isInteger(value) ? 0 : 2;
  const suffix = unit === 'degree' ? '°' : ` ${unit}`;
  return `${value.toFixed(digits)}${suffix}`;
}

function ParameterRow({ parameter }: { parameter: DesignParameter }) {
  const { value, min, max, standard, unit, label } = parameter;
  const span = max - min || 1;
  const at = Math.min(100, Math.max(0, ((value - min) / span) * 100));
  const standardAt = Math.min(100, Math.max(0, ((standard - min) / span) * 100));
  const adjusted = Math.abs(value - standard) > 1e-9;

  return (
    <li className="py-2">
      <div className="flex items-baseline gap-2">
        <span className="flex-1 text-[12.5px] text-foreground/85">{label}</span>
        {adjusted && (
          <span
            className="rounded bg-warning-50 px-1.5 py-px text-[10px] font-semibold tracking-wide text-warning-600 uppercase"
            title={`Material standard: ${formatValue(standard, unit)}`}
          >
            Adjusted
          </span>
        )}
        <span className="font-mono text-[12.5px] font-semibold tabular-nums">
          {formatValue(value, unit)}
        </span>
      </div>
      <div className="relative mt-1.5 h-1 rounded-full bg-muted" aria-hidden>
        <span
          className="absolute top-1/2 h-2.5 w-px -translate-y-1/2 bg-neutral-400"
          style={{ left: `${String(standardAt)}%` }}
        />
        <span
          className={cn(
            'absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-background',
            adjusted ? 'bg-warning-500' : 'bg-primary',
          )}
          style={{ left: `${String(at)}%` }}
        />
      </div>
      <div className="mt-0.5 flex justify-between font-mono text-[10px] text-muted-foreground tabular-nums">
        <span>{formatValue(min, unit)}</span>
        <span>{formatValue(max, unit)}</span>
      </div>
    </li>
  );
}

interface DetailsPanelProps {
  summary: CaseSummary;
}

export function DetailsPanel({ summary }: DetailsPanelProps) {
  const shown = summary.parameters.filter((parameter) => HEADLINE.includes(parameter.key));
  const adjustedCount = shown.filter(
    (parameter) => Math.abs(parameter.value - parameter.standard) > 1e-9,
  ).length;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <Section title="Restorations">
        <table className="w-full text-[12.5px]">
          <thead>
            <tr className="text-left text-[10.5px] tracking-wide text-muted-foreground uppercase">
              <th className="pb-1.5 font-medium">Tooth</th>
              <th className="pb-1.5 font-medium">Type</th>
              <th className="pb-1.5 font-medium">Material</th>
              <th className="pb-1.5 text-right font-medium">Shade</th>
            </tr>
          </thead>
          <tbody>
            {summary.teeth.map((tooth) => (
              <tr key={tooth.tooth} className="border-t border-border">
                <td className="py-1.5 font-mono font-semibold">{tooth.tooth}</td>
                <td className="py-1.5">{tooth.restoration}</td>
                <td className="py-1.5">{tooth.material}</td>
                <td className="py-1.5 text-right font-mono">{tooth.shade ?? '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>

      {shown.length > 0 && (
        <Section
          title="Design parameters"
          aside={
            adjustedCount > 0 ? (
              <span className="text-[11px] text-warning-600">{adjustedCount} adjusted</span>
            ) : undefined
          }
        >
          <ul className="divide-y divide-border/70">
            {shown.map((parameter) => (
              <ParameterRow key={parameter.key} parameter={parameter} />
            ))}
          </ul>
          <p className="mt-2 text-[11px] text-muted-foreground">
            The bar is the material's allowed range; the tick is its standard value.
          </p>
        </Section>
      )}

      {summary.designerNote && (
        <Section title="From the technician">
          <blockquote className="rounded-lg border-l-2 border-primary/60 bg-muted/50 px-3 py-2 text-[12.5px] leading-relaxed">
            {summary.designerNote}
          </blockquote>
          <p className="mt-2 text-[11px] text-muted-foreground">
            {summary.designedBy} ·{' '}
            {new Date(summary.designedAt).toLocaleDateString(undefined, {
              day: 'numeric',
              month: 'long',
              year: 'numeric',
            })}
          </p>
        </Section>
      )}
    </div>
  );
}
